import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Search, X, SlidersHorizontal, Inbox } from "lucide-react";
import api from "@/api";
import ProviderCard from "@/components/ProviderCard";
import FilterSidebar from "@/components/FilterSidebar";
import { useCompare } from "@/hooks/useCompare";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Seo from "@/components/Seo";

const ARR_KEYS = ["provider_type", "category", "industry", "device_type", "connectivity", "sensor", "battery", "commercial", "compliance", "use_case"];

function parseFilters(sp) {
  const f = { q: sp.get("q") || "" };
  ARR_KEYS.forEach((k) => { f[k] = sp.getAll(k); });
  f.country = sp.get("country") || "";
  f.verified_only = sp.get("verified_only") === "true";
  f.featured_only = sp.get("featured_only") === "true";
  f.sort = sp.get("sort") || "relevance";
  f.page = parseInt(sp.get("page") || "1", 10);
  return f;
}

function filtersToParams(f) {
  const params = new URLSearchParams();
  if (f.q) params.set("q", f.q);
  if (f.country) params.set("country", f.country);
  if (f.verified_only) params.set("verified_only", "true");
  if (f.featured_only) params.set("featured_only", "true");
  if (f.sort && f.sort !== "relevance") params.set("sort", f.sort);
  if (f.page && f.page > 1) params.set("page", String(f.page));
  ARR_KEYS.forEach((k) => (f[k] || []).forEach((v) => params.append(k, v)));
  return params;
}

export default function Solutions() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [taxonomies, setTaxonomies] = useState({});
  const filters = useMemo(() => parseFilters(sp), [sp]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const compare = useCompare();
  const [keywordInput, setKeywordInput] = useState(filters.q);

  useEffect(() => { api.get("/taxonomies").then(({ data }) => setTaxonomies(data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = filtersToParams(filters);
    params.set("page_size", "24");
    api.get(`/providers?${params.toString()}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
    // log event
    api.post("/analytics/event", { event_type: "search_performed", metadata: { q: filters.q, filters } }).catch(()=>{});
  }, [sp]);

  const updateFilters = (next) => {
    const merged = { ...filters, ...next, page: 1 };
    setSp(filtersToParams(merged));
  };
  const reset = () => setSp(new URLSearchParams());

  const totalPages = Math.max(1, Math.ceil(data.total / 24));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo
        title="Tracking Solution Providers Directory"
        description="Search and filter verified tracking device manufacturers, GPS trackers, BLE tags, smart label providers, cold chain monitoring systems and asset tracking platforms by use case, technology, sensors, region and commercial model."
      />
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Directory</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mt-1">Tracking solution providers</h1>
          <p className="text-sm text-slate-500 mt-1">
            {loading ? "Loading…" : <><span className="font-mono">{data.total}</span> providers match your filters</>}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={filters.sort} onValueChange={(v) => updateFilters({ sort: v })}>
            <SelectTrigger className="w-44" data-testid="sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Relevance</SelectItem>
              <SelectItem value="featured">Featured first</SelectItem>
              <SelectItem value="name">A → Z</SelectItem>
              <SelectItem value="recent">Recently updated</SelectItem>
            </SelectContent>
          </Select>
          <Sheet>
            <SheetTrigger asChild>
              <button className="md:hidden btn-secondary" data-testid="mobile-filters-toggle"><SlidersHorizontal size={16}/> Filters</button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <FilterSidebar taxonomies={taxonomies} filters={filters} onChange={updateFilters} onReset={reset}/>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Search bar */}
      <form
        onSubmit={(e) => { e.preventDefault(); updateFilters({ q: keywordInput }); }}
        className="flex gap-2 mb-6"
        data-testid="solutions-search-form"
      >
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            placeholder="Search by company, device, technology, sensor…"
            className="pl-10 h-11"
            data-testid="solutions-search-input"
          />
        </div>
        <button type="submit" className="btn-primary h-11">Search</button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        <div className="hidden lg:block">
          <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto thin-scroll pr-2">
            <FilterSidebar taxonomies={taxonomies} filters={filters} onChange={updateFilters} onReset={reset}/>
          </div>
        </div>

        <div>
          {compare.count > 0 && (
            <div className="mb-4 p-3 rounded-md border border-slate-900 bg-slate-900 text-white flex items-center justify-between">
              <div className="text-sm">
                <span className="font-display font-semibold">{compare.count}</span> selected for comparison
              </div>
              <div className="flex gap-2">
                <button onClick={() => compare.clear()} className="text-xs text-white/70 hover:text-white" data-testid="compare-clear">Clear</button>
                <button onClick={() => navigate("/compare")} className="btn-accent text-xs px-3 py-1.5" data-testid="compare-view">View comparison</button>
              </div>
            </div>
          )}

          {!loading && data.items.length === 0 && (
            <div className="card-elevated p-12 text-center">
              <Inbox size={32} className="mx-auto text-slate-300"/>
              <div className="mt-3 font-display font-bold text-xl text-slate-900">No providers match those filters</div>
              <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">Try broadening your search by removing one or two filters, or request help finding a provider.</p>
              <div className="mt-5 flex justify-center gap-3">
                <button className="btn-secondary" onClick={reset} data-testid="empty-reset">Clear filters</button>
                <Link to="/request-quotes" className="btn-primary" data-testid="empty-request">Request help</Link>
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Or <Link to="/submit-provider" className="brand-text font-semibold">submit a missing provider</Link>.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.items.map((p) => (
              <ProviderCard
                key={p.id}
                provider={p}
                isComparing={compare.ids.includes(p.id)}
                onCompare={(prov) => compare.toggle(prov.id)}
                onRequestQuote={(prov) => navigate(`/request-quotes?provider=${prov.slug}`)}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2" data-testid="pagination">
              {Array.from({ length: totalPages }).slice(0, 8).map((_, i) => {
                const pg = i + 1;
                const active = pg === filters.page;
                return (
                  <button
                    key={pg}
                    onClick={() => setSp(filtersToParams({ ...filters, page: pg }))}
                    className={`w-9 h-9 rounded-md text-sm font-semibold ${active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                    data-testid={`page-${pg}`}
                  >
                    {pg}
                  </button>
                );
              })}
              {totalPages > 8 && <span className="text-sm text-slate-500">…</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
